<!--
  SdAllocationPanel.vue  —  SD Time Allocation feature (design doc EMP_SD_Time_Allocation_Design_v0.1, D1-D5, D9, D10)
  NEW component. Target location: web/src/views/apps/campaigns/SdAllocationPanel.vue
  Used by CampaignAdd.vue (edit-mode=false, row-name-key="name")
  and CampaignEdit.vue (edit-mode=true, row-name-key="full_name").

  Props:
    salesDevelopers : the parent's live sales_developers array (Add row shape: {counter, name})
                      (Edit row shape: {counter, full_name, id, uuid, first_name, surname, allocated_hours?})
    startDate/endDate : the parent's campaign dates (Date object or string — parsed defensively)
    campaignId      : null on create; campaign id on edit (sent as excludeCampaignId to the availability check)
    budgetDays      : PO-derived calling-day budget (number or null while POs incomplete)
    editMode        : true on CampaignEdit — enables the compulsory-note dialog on allocation changes (D5)
    rowNameKey      : which row property holds the v-select selection ("name" on Add, "full_name" on Edit)

  Emits:
    "change" -> { allocations: [{salesDeveloperId, name, allocatedHours, allocationNote}],
                  allocatedDays, hasBlocked, valid }
      Fired on every accepted allocation edit and after every availability response.
      Parent must keep the latest payload and (a) merge allocations into the campaign
      save payload, (b) refuse submit while hasBlocked is true (D10).

  Availability calls are made on explicit events only (row blur / date change / mount) — never per keystroke.
-->
<template>
  <div class="sd-allocation-panel">

    <!-- Campaign balance-sheet line (D2 — warn, never block) -->
    <div class="vx-row mb-6" v-if="resolvedRows.length > 0">
      <div class="vx-col sm:w-1/5 w-full">
        <span>SD Day Allocation:</span>
      </div>
      <div class="vx-col sm:w-2/3 w-full">
        <span v-if="budgetDaysNumber != null">
          Budget: <b>{{ formatDays(budgetDaysNumber) }}</b> days
          <span class="ml-5">Allocated: <b>{{ formatDays(allocatedDays) }}</b> days</span>
          <span class="ml-5">Unallocated: <b>{{ formatDays(unallocatedDays) }}</b> days</span>
        </span>
        <span v-else>
          Allocated: <b>{{ formatDays(allocatedDays) }}</b> days
          <span class="text-sm"> (add PO value + daily rate to see the campaign day budget)</span>
        </span>
        <br />
        <span v-if="balanceWarning" :style="{ color: warnColour }" class="text-sm">
          {{ balanceWarning }}
        </span>
      </div>
    </div>

    <!-- Per-SD allocation rows.
         FIX04 2026-08-04 (ROOT CAUSE): vuesax's vs-popup relocates its root
         element to document.body on mount, and Vue SILENTLY SKIPS inserting any
         new element whose next-sibling anchor has left the parent (vue.js
         insert(): `if (nodeOps.parentNode(ref) === parent)`). With vue-loader's
         condensed whitespace, a row added at the END of this list anchored on
         the relocated popup and was silently dropped — "2nd SD has no allocation
         fields". Fix: (a) rows live in their OWN container div so row insertions
         only ever anchor within it; (b) the popup sits ABOVE the lists.
         Reproduced + fix verified in a local harness against vue 2.7.16 +
         vuesax 3.11.1 + production-style compiled templates. -->
    <div class="sd-alloc-rows-container">
    <template v-for="row in renderRows">
      <div class="vx-row mb-2 sd-alloc-row" :key="'sd_alloc_' + row.id">
        <div class="vx-col sm:w-1/5 w-full">
          <span>{{ row.name }} — allocation:</span>
        </div>
        <div class="vx-col sm:w-1/6 w-full">
          <span class="text-sm">Days</span>
          <vs-input type="number" class="w-full" :name="'sd_allocation_days_' + row.id"
            v-model="entries[row.id].days" @input="onDaysInput(row.id)" @blur="onRowBlur(row.id)" />
        </div>
        <div class="vx-col sm:w-1/6 w-full">
          <span class="text-sm">Hours (7 h = 1 day)</span>
          <vs-input type="number" class="w-full" :name="'sd_allocation_hours_' + row.id"
            v-model="entries[row.id].hours" @input="onHoursInput(row.id)" @blur="onRowBlur(row.id)" />
        </div>
        <div class="vx-col sm:w-1/3 w-full">
          <span v-if="entries[row.id].checking" class="text-sm">Checking availability…</span>
          <template v-else-if="entries[row.id].availability">
            <span class="text-sm" :style="{ color: statusColour(entries[row.id].status), fontWeight: 'bold' }">
              {{ statusLabel(entries[row.id].status) }}
            </span>
            <span class="text-sm">
              — {{ entries[row.id].availability.available_days }} available day(s) in window ·
              {{ entries[row.id].availability.free_hours }} h free ·
              implied {{ entries[row.id].availability.implied_hours_per_day }} h/day (standard 7.0, max 10.0)
            </span>
            <br />
            <span v-if="entries[row.id].message" class="text-sm"
              :style="{ color: statusColour(entries[row.id].status) }">
              {{ entries[row.id].message }}
            </span>
          </template>
          <span v-else-if="entries[row.id].checkError" class="text-sm" :style="{ color: blockColour }">
            {{ entries[row.id].checkError }}
          </span>
        </div>
      </div>
    </template>

    <!-- FIX 2026-08-04: a selection we cannot tie to a user record is now shown,
         never silently skipped. -->
    <div class="vx-row mb-2" v-for="u in renderUnresolved" :key="'sd_unres_' + u.index">
      <div class="vx-col sm:w-1/5 w-full">
        <span>{{ u.name }} — allocation:</span>
      </div>
      <div class="vx-col sm:w-2/3 w-full">
        <span class="text-sm" :style="{ color: blockColour }">
          Could not identify this Sales Developer's record, so no allocation can be set.
          Please re-pick them from the dropdown; if it persists, report it (allocation panel: unresolved row).
        </span>
      </div>
    </div>
    </div><!-- /sd-alloc-rows-container (FIX04) -->

    <!-- Compulsory-note dialog (D5) — opens when an existing allocation value changes
         in edit mode. FIX04: kept BELOW the rows container in source, but its element
         self-relocates to document.body (vuesax behaviour) — the container above
         guarantees row insertions never use it as an anchor. -->
    <vs-popup title="Allocation Change — Note Required" :active.sync="notePopupActive">
      <div class="vx-row mb-6">
        <div class="vx-col w-full">
          <span>
            You are changing <b>{{ pendingRowName }}</b>'s allocation from
            <b>{{ formatHoursAndDays(pendingPreviousHours) }}</b> to
            <b>{{ formatHoursAndDays(pendingNewHours) }}</b>.
            Every allocation change is recorded with who, when, old &rarr; new and a compulsory note.
          </span>
        </div>
      </div>
      <div class="vx-row mb-6">
        <div class="vx-col w-full">
          <vs-textarea name="sd_allocation_note" v-model="pendingNote"
            placeholder="Why is this allocation changing? (required)" />
        </div>
      </div>
      <div class="vx-row mb-6">
        <div class="vx-col w-full">
          <vs-button class="mr-3 mb-2" :disabled="!pendingNoteValid" @click="confirmNote">Save Note</vs-button>
          <vs-button class="mr-3 mb-2" color="danger" type="border" @click="cancelNote">Cancel Change</vs-button>
        </div>
      </div>
    </vs-popup>

  </div>
</template>

<script>
import moment from "moment";

var HOURS_PER_DAY = 7;

export default {
  name: "SdAllocationPanel",
  props: {
    salesDevelopers: {
      type: Array,
      required: true
    },
    startDate: {
      type: [String, Date],
      default: null
    },
    endDate: {
      type: [String, Date],
      default: null
    },
    campaignId: {
      type: [String, Number],
      default: null
    },
    budgetDays: {
      type: [Number, String],
      default: null
    },
    editMode: {
      type: Boolean,
      default: false
    },
    rowNameKey: {
      type: String,
      default: "name"
    },
    // FIX 2026-08-04: the parent's Sales Developer option list (allSalesDevelopers).
    // Needed to resolve a row whose v-select wrote a plain STRING (vue-select
    // returns the raw option when the options array holds strings) — such a row
    // carries no uuid of its own, so we look the name up here. Optional: when not
    // supplied the panel behaves exactly as before for object-valued selections.
    optionList: {
      type: Array,
      default: function () { return []; }
    }
  },
  data() {
    return {
      // keyed by sales developer id (uuid): { days, hours, originalHours, lastAcceptedHours,
      //   note, availability, status, message, checking, checkError }
      entries: {},
      // FIX03: the rows the template iterates — PLAIN DATA, rebuilt wholesale by
      // syncEntries() on every change. Replacing the array reference is the most
      // primitive (and unbreakable) reactivity trigger Vue has.
      renderRows: [],
      renderUnresolved: [],
      notePopupActive: false,
      pendingRowId: null,
      pendingRowName: null,
      pendingPreviousHours: null,
      pendingNewHours: null,
      pendingNote: "",
      // vuexy palette
      okColour: "#28C76F",
      warnColour: "#FF9F43",
      blockColour: "#EA5455"
    };
  },
  computed: {
    // Normalise the parent's rows into [{id, name, originalHours}].
    // Handles both parent shapes:
    //  - a freshly picked v-select option object (has .uuid / .value / .label)
    //  - a row loaded from the server on Edit (string full_name + row.uuid/.id, may carry allocated_hours)
    // FIX 2026-08-04: previously any row whose id could not be resolved was
    // dropped SILENTLY (no allocation inputs, no explanation) — the reported
    // "second and subsequent SDs have no allocation fields" symptom. Resolution
    // is now exhaustive (all known id/name key spellings, plus a name lookup
    // against optionList for string-valued selections), and anything still
    // unresolvable is surfaced via unresolvedRows instead of vanishing.
    resolvedRows() {
      var self = this;
      var out = [];
      (this.salesDevelopers || []).forEach(function (row) {
        if (!row) return;
        var r = self.resolveRow(row);
        if (r.id == null) return;
        out.push(r);
      });
      return out;
    },
    // Rows with a selection we could not tie to a user record — rendered as a
    // visible warning so the problem can never be invisible again.
    unresolvedRows() {
      var self = this;
      var out = [];
      (this.salesDevelopers || []).forEach(function (row, index) {
        if (!row) return;
        // Only rows where a selection has actually been made (ignore the empty
        // dropdown a user hasn't picked yet).
        var sel = row[self.rowNameKey];
        if (sel == null || sel === "") return;
        var r = self.resolveRow(row);
        // FIX01b: a selected-but-unidentifiable row is warned about even when
        // no display name could be extracted either (previously it needed a
        // name to be shown — a residual way to disappear silently).
        if (r.id == null) out.push({ index: index, name: r.name || ("Sales Developer " + (index + 1)) });
      });
      return out;
    },
    // FIX02: the rows the template actually iterates — resolved AND having an
    // entry (syncEntries guarantees entries exist; this removes the v-if).
    rowsToRender() {
      var self = this;
      return this.resolvedRows.filter(function (r) { return !!self.entries[r.id]; });
    },
    budgetDaysNumber() {
      if (this.budgetDays === null || this.budgetDays === undefined || this.budgetDays === "") return null;
      var n = parseFloat(this.budgetDays);
      if (isNaN(n) || !isFinite(n)) return null;
      return n;
    },
    allocatedDays() {
      var self = this;
      var totalHours = 0;
      this.resolvedRows.forEach(function (row) {
        var e = self.entries[row.id];
        if (!e) return;
        var h = self.toNumber(e.hours);
        if (h != null) totalHours += h;
      });
      return totalHours / HOURS_PER_DAY;
    },
    unallocatedDays() {
      if (this.budgetDaysNumber == null) return null;
      return this.budgetDaysNumber - this.allocatedDays;
    },
    // D2: over- or under-allocation is warned, never blocked
    balanceWarning() {
      if (this.unallocatedDays == null) return null;
      if (this.unallocatedDays < -0.009) {
        return "Warning: campaign is over-allocated by " + this.formatDays(Math.abs(this.unallocatedDays)) +
          " day(s) against the PO-derived budget.";
      }
      if (this.unallocatedDays > 0.009 && this.allocatedDays > 0) {
        return "Warning: " + this.formatDays(this.unallocatedDays) +
          " budget day(s) not yet allocated to a Sales Developer.";
      }
      return null;
    },
    pendingNoteValid() {
      return this.pendingNote != null && this.pendingNote.trim().length > 0;
    }
  },
  watch: {
    salesDevelopers: {
      deep: true,
      immediate: true,
      handler() {
        this.syncEntries();
      }
    },
    // D9: date change triggers a re-check across all allocated rows and alerts the editor
    startDate() {
      this.onDatesChanged();
    },
    endDate() {
      this.onDatesChanged();
    },
    // treat closing the popup via its X as "cancel change"
    notePopupActive(val) {
      if (val === false && this.pendingRowId != null) {
        this.cancelNote();
      }
    }
  },
  mounted() {
    // Version marker: instantly answers "is the fixed component in this build?"
    // console.info("SdAllocationPanel v1.5 (FIX05 2026-08-05) mounted");
    this.syncEntries();
    // passive initial read-out so existing allocations show their availability on load
    this.recheckAll(false);
    this.emitState();

  },
  // FIX02 diagnostics (temporary — remove once the feature is verified in QA):
  // after every re-render, report the state of each layer so a failure is
  // localisable from the console alone: data (resolved), state (entries),
  // DOM (domRows), and whether the parent's patching has detached us.
  methods: {
    // FIX 2026-08-04: single, exhaustive row resolver used by resolvedRows and
    // unresolvedRows. Handles every shape the two parent pages produce:
    //   Add,  freshly picked  : {counter, name: <option object>}
    //   Add,  string options  : {counter, name: "Clara Hajj"}      <- was dropped
    //   Edit, loaded from DB  : {full_name: "Dyllan Reddi", uuid, id, allocated_hours?}
    //   Edit, freshly picked  : {counter, full_name: <option object>}
    //   Edit, string options  : {counter, full_name: "Clara Hajj"} <- was dropped
    resolveRow(row) {
      var sel = row[this.rowNameKey];
      var id = null;
      var name = null;
      if (sel && typeof sel === "object") {
        id = sel.uuid || sel.value || sel.id || sel.user_id || null;
        name = sel.label || sel.name || sel.full_name ||
          (((sel.first_name || "") + " " + (sel.surname || "")).trim() || null);
      } else if (typeof sel === "string" && sel !== "") {
        name = sel;
        // A row loaded from the server carries its own id...
        id = row.uuid || row.id || row.sales_developer_id || null;
        // ...a row whose v-select wrote a plain string does not: look the
        // display name up in the parent's option list.
        if (id == null) {
          var match = (this.optionList || []).find(function (o) {
            if (!o) return false;
            var label = o.label || o.name || o.full_name ||
              (((o.first_name || "") + " " + (o.surname || "")).trim());
            return label === sel;
          });
          if (match) id = match.uuid || match.value || match.id || match.user_id || null;
        }
      }
      var originalHours = null;
      if (row.allocated_hours !== undefined && row.allocated_hours !== null && row.allocated_hours !== "") {
        originalHours = parseFloat(row.allocated_hours);
        if (isNaN(originalHours)) originalHours = null;
      } else if (row.originalHours !== undefined && row.originalHours !== null && row.originalHours !== "") {
        // Edit page seeds persisted allocations onto the row as originalHours
        // (see SDA_Frontend_guide.md a.2 step 5).
        originalHours = parseFloat(row.originalHours);
        if (isNaN(originalHours)) originalHours = null;
      }
      return { id: id, name: name, originalHours: originalHours };
    },
    toNumber(val) {
      if (val === null || val === undefined || val === "") return null;
      var n = parseFloat(val);
      if (isNaN(n) || !isFinite(n) || n < 0) return null;
      return n;
    },
    formatDays(n) {
      if (n == null) return "-";
      return (Math.round(n * 100) / 100).toFixed(2).replace(/\.00$/, "");
    },
    formatHoursAndDays(hours) {
      if (hours == null) return "unallocated";
      return hours + " h (" + this.formatDays(hours / HOURS_PER_DAY) + " d)";
    },
    // Campaign dates arrive either as a Date (datepicker selection) or as the stored
    // string. DB stores campaign dates as text (design doc §4); a DD-MM-YYYY string
    // must not be fed to Date.parse/moment without an explicit format.
    formatApiDate(value) {
      if (!value) return null;
      if (value instanceof Date) return moment(value).format("YYYY-MM-DD");
      if (typeof value === "string" && /^\d{2}-\d{2}-\d{4}$/.test(value)) {
        return moment(value, "DD-MM-YYYY").format("YYYY-MM-DD");
      }
      var m = moment(value);
      return m.isValid() ? m.format("YYYY-MM-DD") : null;
    },
    statusColour(status) {
      if (status === "block") return this.blockColour;
      if (status === "warn") return this.warnColour;
      return this.okColour;
    },
    statusLabel(status) {
      if (status === "block") return "BLOCKED";
      if (status === "warn") return "STRETCH";
      return "OK";
    },
    syncEntries() {
      var self = this;
      var liveIds = {};
      this.resolvedRows.forEach(function (row) {
        liveIds[row.id] = true;
        if (!self.entries[row.id]) {
          var seedHours = row.originalHours;
          // Rows seeded with a persisted allocation (Edit page loads after mount)
          // get a passive availability read-out once they first appear.
          if (seedHours != null && seedHours > 0) {
            self.$nextTick(function () {
              self.checkAvailability(row.id);
            });
          }
          self.$set(self.entries, row.id, {
            days: seedHours != null ? self.formatDays(seedHours / HOURS_PER_DAY) : "",
            hours: seedHours != null ? String(seedHours) : "",
            originalHours: seedHours,
            lastAcceptedHours: seedHours,
            note: "",
            availability: null,
            status: null,
            message: null,
            checking: false,
            checkError: null
          });
        } else {
          // FIX05 2026-08-05: back-fill a LATE-arriving persisted allocation.
          // On Edit, the SD rows resolve (and entries are created EMPTY) as soon
          // as fetchCampaign lands; the stored allocations arrive later from
          // getSdAllocationSummary and are $set onto the rows as originalHours.
          // The guard above ("only seed a NEW entry") silently discarded that
          // late seed — stored allocations never appeared and an untouched save
          // would have nulled them. The panel now loses neither arrival order:
          // an existing entry the user has NOT touched is seeded in place; if
          // the user already typed, their value is kept but originalHours is
          // still recorded so the D5 compulsory-note dialog arms correctly.
          var e = self.entries[row.id];
          if (e.originalHours == null && row.originalHours != null) {
            e.originalHours = row.originalHours;
            var untouched = e.lastAcceptedHours == null &&
              (e.hours === "" || e.hours == null) &&
              (e.days === "" || e.days == null);
            if (untouched) {
              e.hours = String(row.originalHours);
              e.days = self.formatDays(row.originalHours / HOURS_PER_DAY);
              e.lastAcceptedHours = row.originalHours;
              if (row.originalHours > 0) {
                self.$nextTick(function () {
                  self.checkAvailability(row.id);
                });
              }
            }
          }
        }
      });
      // drop entries for removed / re-selected rows
      Object.keys(this.entries).forEach(function (id) {
        if (!liveIds[id]) {
          self.$delete(self.entries, id);
        }
      });
      // FIX03: rebuild the template's row lists as plain data (whole-array
      // replacement — no computed chain between the data and the template).
      this.renderRows = this.resolvedRows.filter(function (r) { return !!self.entries[r.id]; });
      this.renderUnresolved = this.unresolvedRows.slice();
      this.emitState();
    },
    onDaysInput(id) {
      var e = this.entries[id];
      if (!e) return;
      var d = this.toNumber(e.days);
      e.hours = d != null ? String(Math.round(d * HOURS_PER_DAY * 100) / 100) : "";
    },
    onHoursInput(id) {
      var e = this.entries[id];
      if (!e) return;
      var h = this.toNumber(e.hours);
      e.days = h != null ? this.formatDays(h / HOURS_PER_DAY) : "";
    },
    onRowBlur(id) {
      var e = this.entries[id];
      if (!e) return;
      var hours = this.toNumber(e.hours);
      var last = e.lastAcceptedHours != null ? e.lastAcceptedHours : null;
      if (hours === last) {
        return; // nothing changed
      }
      // D5: an existing (persisted) allocation changing on Edit requires a note before it is accepted
      if (this.editMode && e.originalHours != null) {
        this.openNotePopup(id, last, hours);
        return;
      }
      this.acceptRowValue(id, hours);
    },
    acceptRowValue(id, hours) {
      var e = this.entries[id];
      e.lastAcceptedHours = hours;
      this.emitState();
      this.checkAvailability(id);
    },
    openNotePopup(id, previousHours, newHours) {
      var row = this.resolvedRows.filter(function (r) { return r.id === id; })[0];
      this.pendingRowId = id;
      this.pendingRowName = row ? row.name : "";
      this.pendingPreviousHours = previousHours;
      this.pendingNewHours = newHours;
      this.pendingNote = this.entries[id].note || "";
      this.notePopupActive = true;
    },
    confirmNote() {
      if (!this.pendingNoteValid) return;
      var e = this.entries[this.pendingRowId];
      var id = this.pendingRowId;
      if (e) {
        e.note = this.pendingNote.trim();
      }
      this.closeNotePopup();
      if (e) {
        this.acceptRowValue(id, this.toNumber(e.hours));
      }
    },
    cancelNote() {
      var e = this.entries[this.pendingRowId];
      if (e) {
        // revert to the last accepted value
        e.hours = e.lastAcceptedHours != null ? String(e.lastAcceptedHours) : "";
        this.onHoursInput(this.pendingRowId);
      }
      this.closeNotePopup();
      this.emitState();
    },
    closeNotePopup() {
      this.pendingRowId = null;
      this.pendingRowName = null;
      this.pendingPreviousHours = null;
      this.pendingNewHours = null;
      this.pendingNote = "";
      if (this.notePopupActive) this.notePopupActive = false;
    },
    onDatesChanged() {
      var self = this;
      var anyAllocated = this.resolvedRows.some(function (row) {
        var e = self.entries[row.id];
        return e && self.toNumber(e.hours) != null && self.toNumber(e.hours) > 0;
      });
      this.recheckAll(anyAllocated);
    },
    recheckAll(notify) {
      var self = this;
      var checked = false;
      this.resolvedRows.forEach(function (row) {
        var e = self.entries[row.id];
        if (!e) return;
        var h = self.toNumber(e.hours);
        if (h != null && h > 0) {
          checked = true;
          self.checkAvailability(row.id);
        }
      });
      if (notify && checked && this.$vs && this.$vs.notify) {
        this.$vs.notify({
          color: "warning",
          title: "Availability re-checked",
          text: "The campaign dates changed — Sales Developer availability has been re-checked against the new window."
        });
      }
    },
    checkAvailability(id) {
      var self = this;
      var e = this.entries[id];
      if (!e) return;
      var hours = this.toNumber(e.hours);
      var startDate = this.formatApiDate(this.startDate);
      var endDate = this.formatApiDate(this.endDate);
      if (hours == null || hours === 0) {
        e.availability = null;
        e.status = null;
        e.message = null;
        e.checkError = null;
        this.emitState();
        return;
      }
      if (!startDate || !endDate) {
        e.availability = null;
        e.status = null;
        e.message = null;
        e.checkError = "Set the campaign start and end dates to check availability.";
        this.emitState();
        return;
      }
      var payload = {
        salesDeveloperId: id,
        requestedHours: hours,
        startDate: startDate,
        endDate: endDate
      };
      if (this.campaignId != null) {
        payload.excludeCampaignId = this.campaignId;
      }
      e.checking = true;
      e.checkError = null;
      this.$store
        .dispatch("campaignsManagement/getSdAvailability", payload)
        .then(function (res) {
          e.checking = false;
          // Controller wraps the engine result: {status:'success', data:{...}}
          // (verified against SDA6_controller.patch) — unwrap one level.
          var data = (res.data && res.data.data) ? res.data.data : (res.data || {});
          e.availability = data;
          e.status = data.status || null;
          e.message = data.message || null;
          self.emitState();
        })
        .catch(function (err) {
          e.checking = false;
          e.availability = null;
          e.status = null;
          e.checkError = "Availability check failed — try again.";
          self.emitState();
          if (err && err.response && err.response.status === 401) {
            self.$router.replace("/login");
          }
        });
    },
    emitState() {
      var self = this;
      var allocations = [];
      var hasBlocked = false;
      this.resolvedRows.forEach(function (row) {
        var e = self.entries[row.id];
        if (!e) return;
        var hours = self.toNumber(e.hours);
        allocations.push({
          salesDeveloperId: row.id,
          name: row.name,
          allocatedHours: hours,
          allocationNote: e.note && e.note !== "" ? e.note : null
        });
        if (e.status === "block") hasBlocked = true;
      });
      this.$emit("change", {
        allocations: allocations,
        allocatedDays: this.allocatedDays,
        hasBlocked: hasBlocked,
        valid: !hasBlocked
      });
    }
  }
};
</script>

<style scoped>
.sd-allocation-panel .text-sm {
  font-size: 0.85rem;
}
</style>